import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, Clock, Edit2, Trash2, CheckCircle, Bell, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ReminderModal from '@/components/ReminderModal';
import { remindersQueue } from '@/services/wellnessQueues';
import { SyncStatusPill } from '@/components/SyncStatusPill';
import { isOnline } from '@/services/localFirstQueue';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyReminders = () => {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [filter, setFilter] = useState('all'); // all, upcoming, completed

  // Guard against StrictMode dev double-invoke / accidental duplicate fires
  // within the same mount cycle. Resets on unmount/remount, so back-navigation
  // still triggers a fresh load. CRUD-triggered refetches still call
  // loadReminders() directly and are not affected by this guard.
  const didInitRef = useRef(false);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadReminders();
  }, []);

  const loadReminders = async () => {
    // Cached-first render: hydrate from the local snapshot (if any) before
    // the network round-trip so the list never flashes empty on reload.
    try {
      const cached = JSON.parse(localStorage.getItem('healthloop.reminders.cache') || 'null');
      if (Array.isArray(cached) && cached.length > 0) setReminders(cached);
    } catch (_) {}

    if (!isOnline()) {
      // Offline — keep cached list, drop the spinner, surface honest status.
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/health-reminders`, {
        withCredentials: true,
      });
      const list = response.data.reminders || [];
      setReminders(list);
      try { localStorage.setItem('healthloop.reminders.cache', JSON.stringify(list)); } catch (_) {}
    } catch (error) {
      console.error('Error loading reminders:', error);
      // Only show the error toast if we don't already have a cached list to
      // fall back to — otherwise the user sees their data anyway.
      const cached = JSON.parse(localStorage.getItem('healthloop.reminders.cache') || '[]');
      if (cached.length === 0) toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReminder = async (reminderData) => {
    const isUpdate = !!reminderData.id;
    // Optimistic local mutation so the UI feels instant even on the tube.
    setReminders((prev) => {
      if (isUpdate) {
        const next = prev.map((r) => (r.id === reminderData.id ? { ...r, ...reminderData } : r));
        try { localStorage.setItem('healthloop.reminders.cache', JSON.stringify(next)); } catch (_) {}
        return next;
      }
      const tempId = `temp-${Date.now()}`;
      const next = [...prev, { ...reminderData, id: tempId, _pending: true }];
      try { localStorage.setItem('healthloop.reminders.cache', JSON.stringify(next)); } catch (_) {}
      return next;
    });

    const op = isUpdate ? 'update' : 'create';
    const result = await remindersQueue.enqueueAndSync(
      isUpdate
        ? { op, id: reminderData.id, data: reminderData }
        : { op, data: reminderData },
    );

    setShowModal(false);
    setEditingReminder(null);

    if (result.ok) {
      toast.success(isUpdate ? 'Reminder updated! ✅' : 'Reminder created! 🔔');
      // Refresh from server so any server-assigned ID lands in state.
      if (!isUpdate) await loadReminders();
    } else if (result.queued) {
      toast.message('Saved locally. Will sync when you reconnect.');
    } else {
      toast.error('Failed to save reminder');
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!window.confirm('Are you sure you want to delete this reminder?')) {
      return;
    }
    // Optimistic remove
    setReminders((prev) => {
      const next = prev.filter((r) => r.id !== reminderId);
      try { localStorage.setItem('healthloop.reminders.cache', JSON.stringify(next)); } catch (_) {}
      return next;
    });
    const result = await remindersQueue.enqueueAndSync({ op: 'delete', id: reminderId });
    if (result.ok) toast.success('Reminder deleted');
    else if (result.queued) toast.message('Deletion saved locally. Will sync when you reconnect.');
    else toast.error('Failed to delete reminder');
  };

  const handleMarkComplete = async (reminderId) => {
    // Optimistic local flag — the actual server response will reconcile on
    // next loadReminders() once online.
    setReminders((prev) => {
      const next = prev.map((r) => (r.id === reminderId ? { ...r, completed: true } : r));
      try { localStorage.setItem('healthloop.reminders.cache', JSON.stringify(next)); } catch (_) {}
      return next;
    });
    const result = await remindersQueue.enqueueAndSync({ op: 'complete', id: reminderId });
    if (result.ok) toast.success('Marked as completed! ✅');
    else if (result.queued) toast.message('Marked locally. Will sync when you reconnect.');
    else toast.error('Failed to update reminder');
  };

  const handleEdit = (reminder) => {
    setEditingReminder(reminder);
    setShowModal(true);
  };

  const getModuleBadgeColor = (module) => {
    const colors = {
      men: 'bg-blue-100 text-blue-800',
      women: 'bg-pink-100 text-pink-800',
      children: 'bg-green-100 text-green-800',
      maternal: 'bg-purple-100 text-purple-800',
      general: 'bg-gray-100 text-gray-800'
    };
    return colors[module] || colors.general;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isUpcoming = (dateStr) => {
    const reminderDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return reminderDate >= today;
  };

  const filteredReminders = reminders.filter(reminder => {
    if (filter === 'upcoming') return !reminder.completed && isUpcoming(reminder.date);
    if (filter === 'completed') return reminder.completed;
    return true;
  });

  const sortedReminders = [...filteredReminders].sort((a, b) => {
    if (a.completed !== b.completed) {
      return a.completed ? 1 : -1;
    }
    return new Date(a.date) - new Date(b.date);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading reminders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-20 md:pb-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Health Reminders</h1>
              <p className="text-sm text-gray-600">Manage your health appointments and checkups</p>
              <div className="mt-2">
                <SyncStatusPill queue={remindersQueue} data-testid="reminders-sync-status" />
              </div>
            </div>
          </div>
          <Button
            onClick={() => {
              setEditingReminder(null);
              setShowModal(true);
            }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Reminder
          </Button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            All ({reminders.length})
          </button>
          <button
            onClick={() => setFilter('upcoming')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              filter === 'upcoming'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Upcoming ({reminders.filter(r => !r.completed && isUpcoming(r.date)).length})
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              filter === 'completed'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Completed ({reminders.filter(r => r.completed).length})
          </button>
        </div>

        {/* Reminders List */}
        {sortedReminders.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Bell className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {filter === 'completed' ? 'No completed reminders' : 'No reminders yet'}
              </h3>
              <p className="text-gray-600 mb-6">
                {filter === 'completed' 
                  ? 'Complete your health appointments to see them here'
                  : 'Create your first health reminder to stay on track'}
              </p>
              {filter !== 'completed' && (
                <Button
                  onClick={() => setShowModal(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Reminder
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {sortedReminders.map(reminder => (
              <Card
                key={reminder.id}
                className={`${reminder.completed ? 'bg-gray-50 opacity-75' : 'bg-white'} hover:shadow-md transition-shadow`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className={`font-semibold ${reminder.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                          {reminder.title}
                        </h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getModuleBadgeColor(reminder.module)}`}>
                          {reminder.module}
                        </span>
                        {reminder.completed && (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDate(reminder.date)}</span>
                        </div>
                        {reminder.time && (
                          <div className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            <span>{reminder.time}</span>
                          </div>
                        )}
                      </div>
                      
                      {reminder.notes && (
                        <p className="text-sm text-gray-600 mt-2">{reminder.notes}</p>
                      )}
                    </div>

                    <div className="flex gap-2">
                      {!reminder.completed && (
                        <button
                          onClick={() => handleMarkComplete(reminder.id)}
                          className="p-2 hover:bg-green-50 rounded-lg transition-colors"
                          title="Mark as completed"
                        >
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(reminder)}
                        className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="h-5 w-5 text-blue-600" />
                      </button>
                      <button
                        onClick={() => handleDeleteReminder(reminder.id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-5 w-5 text-red-600" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Reminder Modal */}
      <ReminderModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingReminder(null);
        }}
        onSave={handleSaveReminder}
        initialData={editingReminder}
        module={editingReminder?.module || 'general'}
      />
    </div>
  );
};

export default MyReminders;
